import api from './api';

export interface SalesCampaign {
  id: string;
  name: string;
  description?: string;
  targetIndustries?: string[];
  targetRegion: string;
  targetCriteria?: {
    nearLocations?: string;
    specificLocations?: string[];
    additionalCriteria?: string;
  };
  searchQuery?: string;
  researchPrompt?: string;
  researchResults?: string;
  status: 'draft' | 'researching' | 'completed' | 'failed';
  icpId?: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
  contacts?: ContactLead[];
  _count?: {
    contacts: number;
  };
}

export interface ContactLead {
  id: string;
  campaignId: string;
  /** Firmenakte, als die der Kontakt uebernommen wurde. */
  companyId?: string | null;
  companyName: string;
  industry?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  region?: string;
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  location?: string;
  contactPerson?: string;
  jobTitle?: string;
  linkedInUrl?: string;
  notes?: string;
  isQualified: boolean;
  qualificationNotes?: string;
  icebreakerEmail?: string;
  icebreakerSubject?: string;
  icebreakerLinkedin?: string;
  icebreakerVariants?: string; // JSON: { email: Variant[], linkedin: Variant[] }
  icebreakerGeneratedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateContactIcebreakersInput {
  contactIds?: string[];
  channels?: ('email' | 'linkedin')[];
  autoSources?: ('research' | 'website' | 'signal')[];
  tone?: string;
  language?: string;
  goal?: string;
  icpId?: string;
  personaId?: string;
  offerId?: string;
  variantCount?: number; // 1–3 Varianten je Kanal (A/B-Test)
}

export interface GenerateContactIcebreakersResult {
  generated: number;
  failed: number;
  details: Array<{ company: string; success: boolean; error?: string }>;
}

export interface CreateSalesCampaignInput {
  name: string;
  description?: string;
  targetIndustries?: string[];
  targetRegion: string;
  targetCriteria?: {
    nearLocations?: string;
    specificLocations?: string[];
    additionalCriteria?: string;
  };
  icpId?: string;
}

export interface UpdateSalesCampaignInput {
  name?: string;
  description?: string;
  targetIndustries?: string[];
  targetRegion?: string;
  targetCriteria?: {
    nearLocations?: string;
    specificLocations?: string[];
    additionalCriteria?: string;
  };
  status?: string;
}

export interface CreateContactLeadInput {
  campaignId: string;
  companyName: string;
  industry?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  region?: string;
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  location?: string;
  contactPerson?: string;
  jobTitle?: string;
  linkedInUrl?: string;
  notes?: string;
}

export interface UpdateContactLeadInput {
  companyName?: string;
  industry?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  region?: string;
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  location?: string;
  contactPerson?: string;
  jobTitle?: string;
  linkedInUrl?: string;
  notes?: string;
  isQualified?: boolean;
  qualificationNotes?: string;
  icebreakerEmail?: string | null;
  icebreakerSubject?: string | null;
  icebreakerLinkedin?: string | null;
}

export interface ResearchResult {
  campaign: SalesCampaign;
  researchResults: {
    summary: string;
    totalCompanies: number;
    companies: any[];
  };
  contactsCreated: number;
}

class SalesCampaignService {
  // Campaign methods
  async createCampaign(input: CreateSalesCampaignInput): Promise<SalesCampaign> {
    const response = await api.post('/sales-campaigns/campaigns', input);
    return response.data;
  }

  async getCampaigns(status?: string): Promise<SalesCampaign[]> {
    const response = await api.get('/sales-campaigns/campaigns', {
      params: status ? { status } : {},
    });
    // Immer ein Array zurueckgeben: liefert die API ausnahmsweise etwas
    // anderes (z. B. eine HTML-Fehlerseite ueber den SPA-Fallback oder einen
    // Paginierungs-Umschlag), wuerde sonst kampagnen.reduce/.map in der
    // Seite werfen und die ganze App auf eine weisse Seite kippen.
    return Array.isArray(response.data) ? response.data : [];
  }

  async getCampaign(id: string): Promise<SalesCampaign> {
    const response = await api.get(`/sales-campaigns/campaigns/${id}`);
    return response.data;
  }

  async updateCampaign(id: string, input: UpdateSalesCampaignInput): Promise<SalesCampaign> {
    const response = await api.put(`/sales-campaigns/campaigns/${id}`, input);
    return response.data;
  }

  async deleteCampaign(id: string): Promise<void> {
    await api.delete(`/sales-campaigns/campaigns/${id}`);
  }

  async researchContacts(id: string): Promise<ResearchResult> {
    const response = await api.post(`/sales-campaigns/campaigns/${id}/research`);
    return response.data;
  }

  // Contact lead methods
  async createContactLead(input: CreateContactLeadInput): Promise<ContactLead> {
    const response = await api.post('/sales-campaigns/contacts', input);
    return response.data;
  }

  async getContactLeads(campaignId: string): Promise<ContactLead[]> {
    const response = await api.get(`/sales-campaigns/campaigns/${campaignId}/contacts`);
    // Siehe getCampaigns: die Detailseite mappt dieses Ergebnis, also nie
    // etwas anderes als ein Array herausgeben.
    return Array.isArray(response.data) ? response.data : [];
  }

  async getContactLead(id: string): Promise<ContactLead> {
    const response = await api.get(`/sales-campaigns/contacts/${id}`);
    return response.data;
  }

  async updateContactLead(id: string, input: UpdateContactLeadInput): Promise<ContactLead> {
    const response = await api.put(`/sales-campaigns/contacts/${id}`, input);
    return response.data;
  }

  /** Kontakt als Firma in die Akte uebernehmen; die Zuordnung bleibt am Kontakt. */
  async alsFirma(id: string): Promise<{ company: { id: string; name: string }; contact: ContactLead }> {
    const response = await api.post(`/sales-campaigns/contacts/${id}/als-firma`);
    return response.data;
  }

  async deleteContactLead(id: string): Promise<void> {
    await api.delete(`/sales-campaigns/contacts/${id}`);
  }

  async generateContactIcebreakers(
    campaignId: string,
    input: GenerateContactIcebreakersInput = {}
  ): Promise<GenerateContactIcebreakersResult> {
    const response = await api.post(
      `/sales-campaigns/campaigns/${campaignId}/icebreakers`,
      input
    );
    return response.data;
  }

  async exportContacts(campaignId: string, format: 'json' | 'csv' = 'json'): Promise<any> {
    const response = await api.get(`/sales-campaigns/campaigns/${campaignId}/export`, {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    });
    return response.data;
  }

  // Helper method to download CSV
  downloadCSV(csvData: Blob, filename: string) {
    const url = window.URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

const salesCampaignService = new SalesCampaignService();
export default salesCampaignService;
